<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRegionsApi } from '@/composables/useRegionsApi';
import type { Region } from '@/typings/regions/Region';
import { DEFAULT_RENTAL_UNIT_STATUSES, DEFAULT_RENTAL_VALID_UNIT_STATUSES } from '@/constants/rentalConstants';
import { DEFAULT_PURCHASE_UNIT_STATUSES, DEFAULT_PURCHASE_VALID_UNIT_STATUSES } from '@/constants/purchaseConstants';
import { ListingTypeEnum } from '@/enums/ListingType';

// Interface pour les régions avec option par défaut
interface RegionWithDefault {
  id: number | null;
  name: string;
  localizedName?: string;
}

interface RegionSearchFieldProps {
  modelValue?: number | null
  label?: string
  placeholder?: string
  listingType?: ListingTypeEnum
}

const props = withDefaults(defineProps<RegionSearchFieldProps>(), {
  modelValue: null,
  label: '',
  placeholder: ''
});

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
}>();

const { t, locale } = useI18n();
const { getAllRegions } = useRegionsApi();

// Utilise le même type Region que dans RentalFiltersBar
const regions = ref<Region[]>([]);
const isOpen = ref(false);
const searchTerm = ref('');
const inputRef = ref<HTMLInputElement>();

// Liste des régions avec option par défaut
const regionsWithDefault = computed(() => {
  const defaultOption = {
    id: null,
    name: t('forms.rentalFilters.city'),
    localizedName: t('forms.rentalFilters.city')
  };
  return [defaultOption, ...regions.value];
});

// Vérifier si une région est sélectionnée
const isSelected = (regionId: number | null) => {
  // Si c'est l'option par défaut (id null), elle est cochée quand aucune région n'est sélectionnée
  if (regionId === null) {
    return props.modelValue === null;
  }
  return props.modelValue === regionId;
};

// Région sélectionnée calculée
const selectedRegion = computed(() => {
  return regions.value.find(region => region.id === props.modelValue);
});

// Affichage de la valeur dans l'input
const displayValue = computed(() => {
  if (selectedRegion.value) {
    return selectedRegion.value.localizedName || selectedRegion.value.name;
  }
  return searchTerm.value || t('forms.rentalFilters.city');
});

// Filtrage des régions basé sur la recherche
const filteredRegions = computed(() => {
  if (!searchTerm.value) {
    return regionsWithDefault.value;
  }
  return regionsWithDefault.value.filter(region => {
    const name = region.localizedName || region.name;
    return name.toLowerCase().includes(searchTerm.value.toLowerCase());
  });
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchTerm.value = '';
    setTimeout(() => {
      inputRef.value?.focus();
    }, 0);
  }
};

const toggleRegion = (region: RegionWithDefault) => {
  // Si c'est l'option par défaut (id null), effacer la sélection
  if (region.id === null) {
    emit('update:modelValue', null);
    isOpen.value = false;
    searchTerm.value = '';
    return;
  }
  
  emit('update:modelValue', region.id);
  isOpen.value = false;
  searchTerm.value = '';
};

const clearSelection = () => {
  emit('update:modelValue', null);
  searchTerm.value = '';
};

const handleInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  searchTerm.value = target.value;
  if (!isOpen.value) {
    isOpen.value = true;
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchTerm.value = '';
};

// Fermer le dropdown quand on clique à l'extérieur
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element;
  if (!target.closest('.search-field')) {
    closeDropdown();
  }
};

onMounted(async () => {
  // Charger les régions au montage du composant (même logique que RentalFiltersBar)
  try {
    regions.value = await getAllRegions({
      locale: locale.value.substring(0, 2),
      unitStatuses: props.listingType === ListingTypeEnum.Rental ? DEFAULT_RENTAL_VALID_UNIT_STATUSES : DEFAULT_PURCHASE_VALID_UNIT_STATUSES,
      listingType: props.listingType === ListingTypeEnum.Rental ? 1 : 0
    });
  } catch (error) {
    console.warn('Failed to load regions:', error);
    regions.value = [];
  }

  // Écouter les clics à l'extérieur avec un délai pour éviter les conflits
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside);
  }, 100);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="search-field">
    <label class="field-label">
      {{ props.label || t('forms.rentalFilters.cityTitle') }}
    </label>
    <div class="input-wrapper">
      <input
        ref="inputRef"
        type="text"
        class="search-input"
        :value="displayValue"
        :placeholder="props.placeholder || t('forms.rentalFilters.city')"
        @click="toggleDropdown"
        @input="handleInputChange" />
      <button
        type="button"
        class="dropdown-toggle"
        @click="toggleDropdown">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          :class="{ 'rotated': isOpen }">
          <path d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
        </svg>
      </button>
      <button
        v-if="selectedRegion"
        type="button"
        class="clear-button"
        @click="clearSelection">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 384 512">
          <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.4c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
        </svg>
      </button>
    </div>

    <!-- Dropdown des options -->
    <div
      v-if="isOpen"
      class="dropdown-menu">
      <div
        v-if="filteredRegions.length === 0"
        class="no-results">
        {{ t('browsingApp.regions.noRegionFound') }}
      </div>
      <label
        v-for="region in filteredRegions"
        :key="region.id || 'default'"
        class="dropdown-item checkbox-item">
        <input
          type="checkbox"
          :checked="isSelected(region.id)"
          @change="toggleRegion(region)"
          class="checkbox" />
        <span class="checkmark"></span>
        <span class="label-text">{{ region.localizedName || region.name }}</span>
      </label>
    </div>
  </div>
</template>
<style scoped lang="scss">
@import './search-field-styles.scss';
</style>